import React, { useState, useEffect } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Save, Loader2, Key } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/api';
import { useServiceConnections, clearServiceConnectionCache } from '@/hooks/useServiceConnections';

const SERVICE_MAP: Record<string, string> = {
    "S3": "aws_s3", 
    "s3": "aws_s3",
    "AzureBlob": "azure_blob", 
    "azure": "azure_blob",
    "SharePoint": "sharepoint", 
    "sharepoint": "sharepoint",
    "SFTP": "sftp", 
    "sftp": "sftp",
    "GDrive": "gdrive", 
    "gdrive": "gdrive",
    "OneDrive": "onedrive", 
    "onedrive": "onedrive",
    "Dropbox": "dropbox",
    "dropbox": "dropbox",
    // HubSpot CRM
    "HubSpot": "hubspot",
    "hubspot": "hubspot",
    "HUBSPOT": "hubspot",
};

interface IntegrationConnectionUIProps {
    protocol: string;
    connectionId: string;
    onChangeConnectionId: (val: string) => void;
    newConnectionLabel?: string;
    newConnectionFields?: Record<string, string>;
    onChangeNewLabel?: (v: string) => void;
    onChangeNewFields?: (v: Record<string, string>) => void;
}

export const IntegrationConnectionUI = ({
    protocol, connectionId, onChangeConnectionId,
    newConnectionLabel, newConnectionFields,
    onChangeNewLabel, onChangeNewFields
}: IntegrationConnectionUIProps) => {
    // Wait for changes to connectionId props to sync external resets
    const [connectionMode, setConnectionMode] = useState<'default' | 'existing' | 'new'>(
        connectionId === 'new' ? 'new' : (connectionId ? 'existing' : 'default')
    );
    
    useEffect(() => {
        // Only reset if there's no active connectionId — don't wipe a saved selection on protocol change
        if (!connectionId) {
            setConnectionMode('default');
        }
    }, [protocol]);

    const [isSaving, setIsSaving] = useState(false);
    // Use lifted state if provided by parent (survives re-renders), else fall back to local state
    const [_localLabel, _setLocalLabel] = useState('');
    const [_localFields, _setLocalFields] = useState<Record<string, string>>({});
    const newLabel = newConnectionLabel !== undefined ? newConnectionLabel : _localLabel;
    const newFields = newConnectionFields !== undefined ? newConnectionFields : _localFields;
    const setNewLabel = onChangeNewLabel ?? _setLocalLabel;
    const setNewFields = onChangeNewFields ?? _setLocalFields;

    const serviceKey = SERVICE_MAP[protocol];
    const { connections, loading: loadingConns } = useServiceConnections(serviceKey);

    const handleModeChange = (val: string) => {
        setConnectionMode(val as any);
        if (val === 'default') {
            onChangeConnectionId('');
        } else if (val === 'new') {
            onChangeConnectionId('new');
        } else if (val === 'existing') {
            if (connections && connections.length > 0) {
                onChangeConnectionId(connections[0].id);
            }
        }
    };

    const handleSaveNewConnection = async () => {
        if (!newLabel || !newLabel.trim()) {
            toast.error("⚠️ Please enter a Connection Name before saving (e.g. 'AIDAS SharePoint')");
            return;
        }

        setIsSaving(true);
        try {
            const token = sessionStorage.getItem('access_token');
            if (!token) throw new Error("Not authenticated");
            let tenantId = 'platform';
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                tenantId = payload.tenant_id || payload.tenantId || 'platform';
            } catch (e) {}

            const res = await apiFetch(`/integrations/tenants/${tenantId}/api-keys`, null, {
                method: 'POST',
                body: JSON.stringify({
                    service: serviceKey,
                    label: newLabel,
                    is_default: false,
                    key_values: { ...newFields }
                })
            });

            if (res && res.id) {
                toast.success("Connection saved successfully!");
                clearServiceConnectionCache(serviceKey);
                onChangeConnectionId(res.id);
                setConnectionMode('existing');
                // Clear new connection form state
                setNewLabel('');
                setNewFields({});
            }
        } catch (e: any) {
            toast.error(e.message || "Failed to save connection");
        } finally {
            setIsSaving(false);
        }
    };

    if (protocol === 'Local' || protocol === 'local') return null;

    return (
        <div className="space-y-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-md border border-gray-100 dark:border-slate-700/50 my-4">
            <div className="space-y-1">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest pl-1">Authentication / Connection</label>
                <Select value={connectionMode} onValueChange={handleModeChange}>
                    <SelectTrigger className="h-7 text-xs bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 shadow-sm">
                        <Key className="w-3 h-3 mr-2 text-gray-400" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 z-50">
                        <SelectItem value="default">⚡ Use Default Integration</SelectItem>
                        <SelectItem value="existing">🔍 Select Existing Connection</SelectItem>
                        <SelectItem value="new">➕ Create New Connection</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {connectionMode === 'existing' && (
                <div className="space-y-1 pt-1">
                    {loadingConns ? (
                        <div className="text-xs text-gray-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
                    ) : connections.length === 0 ? (
                        <div className="text-[11px] text-amber-500 dark:text-amber-400 p-2 bg-amber-50 dark:bg-amber-900/10 rounded-md border border-amber-100 dark:border-amber-900/30">
                            No saved connections found for {protocol}.
                        </div>
                    ) : (
                        <Select value={connectionId || ''} onValueChange={(v) => onChangeConnectionId(v)}>
                            <SelectTrigger className="h-7 text-xs bg-white dark:bg-slate-900 border-indigo-200 dark:border-indigo-800/50">
                                <SelectValue placeholder="Select a connection..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-slate-800 z-50">
                                {connections.map((c: any) => (
                                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>
            )}

            {connectionMode === 'new' && (
                <div className="space-y-3 pt-2 mt-2 border-t border-gray-200 dark:border-slate-700">
                    <div className="space-y-1">
                        <label className="text-[10px] text-gray-500">Connection Name</label>
                        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="h-7 text-xs" placeholder="e.g. Prod S3" />
                    </div>

                    {(protocol === 'S3' || protocol === 's3') && (
                        <>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Region</label>
                                <Input value={newFields.region || ''} onChange={(e) => setNewFields({...newFields, region: e.target.value})} className="h-7 text-xs" placeholder="us-east-1" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Access Key</label>
                                <Input value={newFields.aws_access_key_id || ''} onChange={(e) => setNewFields({...newFields, aws_access_key_id: e.target.value})} className="h-7 text-xs" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Secret Key</label>
                                <Input type="password" value={newFields.aws_secret_access_key || ''} onChange={(e) => setNewFields({...newFields, aws_secret_access_key: e.target.value})} className="h-7 text-xs" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Custom Endpoint (MinIO)</label>
                                <Input value={newFields.host || ''} onChange={(e) => setNewFields({...newFields, host: e.target.value})} className="h-7 text-xs" placeholder="https://minio.local" />
                            </div>
                        </>
                    )}

                    {(protocol === 'AzureBlob' || protocol === 'azure') && (
                        <div className="space-y-1">
                            <label className="text-[10px] text-gray-500">Connection String</label>
                            <Input type="password" value={newFields.connection_string || ''} onChange={(e) => setNewFields({...newFields, connection_string: e.target.value})} className="h-7 text-xs" placeholder="DefaultEndpointsProtocol=https;..." />
                        </div>
                    )}

                    {(protocol === 'SharePoint' || protocol === 'sharepoint') && (
                        <>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Tenant ID</label>
                                <Input value={newFields.tenant_id || ''} onChange={(e) => setNewFields({...newFields, tenant_id: e.target.value})} className="h-7 text-xs" placeholder="xxxx-xxxx-xxxx" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Client ID</label>
                                <Input value={newFields.client_id || ''} onChange={(e) => setNewFields({...newFields, client_id: e.target.value})} className="h-7 text-xs" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500">Client Secret</label>
                                <Input type="password" value={newFields.client_secret || ''} onChange={(e) => setNewFields({...newFields, client_secret: e.target.value})} className="h-7 text-xs" />
                            </div>
                        </>
                    )}

                    {/* HubSpot: Private App Token */}
                    {(protocol === 'HubSpot' || protocol === 'hubspot') && (
                        <>
                            <div className="space-y-1">
                                <label className="text-[10px] text-gray-500 flex items-center gap-1">
                                    Private App Token
                                    <a
                                        href="https://developers.hubspot.com/docs/api/private-apps"
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-blue-500 underline text-[9px] ml-1"
                                    >
                                        How to create →
                                    </a>
                                </label>
                                <Input
                                    type="password"
                                    value={newFields.access_token || ''}
                                    onChange={(e) => setNewFields({...newFields, access_token: e.target.value})}
                                    className="h-7 text-xs font-mono"
                                    placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                                <p className="text-[9px] text-gray-400 pl-1">
                                    Go to HubSpot → Settings → Integrations → Private Apps → Create a private app.
                                </p>
                            </div>
                        </>
                    )}

                    <Button onClick={handleSaveNewConnection} disabled={isSaving || !newLabel} size="sm" className="w-full h-7 text-xs bg-indigo-500 hover:bg-indigo-600">
                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3 h-3 mr-2" />}
                        Save to Integrations
                    </Button>
                </div>
            )}
        </div>
    );
};
